<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue';
import { useI18n } from 'vue-i18n';
import billingDashboardAPI from 'dashboard/api/billingDashboard';
import Button from 'dashboard/components-next/button/Button.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';

defineProps({
  isCollapsed: { type: Boolean, default: false },
});

const POLL_INTERVAL_MS = 5 * 60 * 1000;

const { t } = useI18n();

const isEnabled = ref(true);
const data = ref(null);
const isLoading = ref(true);
let pollTimer = null;

const fetchData = async () => {
  try {
    const response = await billingDashboardAPI.get();
    isEnabled.value = response.data?.enabled !== false;
    data.value = response.data?.data || null;
  } catch {
    isEnabled.value = false;
  } finally {
    isLoading.value = false;
  }
};

onMounted(() => {
  fetchData();
  pollTimer = setInterval(fetchData, POLL_INTERVAL_MS);
});

onUnmounted(() => {
  if (pollTimer) clearInterval(pollTimer);
});

const aiUsed = computed(() => Number(data.value?.ai_responses_used || 0));
const aiTotal = computed(() => {
  const limit = Number(data.value?.ai_responses_limit || 0);
  const prepaid = Number(data.value?.ai_responses_prepaid || 0);
  return limit + prepaid;
});
const aiPercent = computed(() => {
  if (!aiTotal.value) return 0;
  return Math.min(100, Math.round((aiUsed.value / aiTotal.value) * 100));
});
const isAiCritical = computed(() => aiPercent.value >= 90);
const isAiWarning = computed(() => aiPercent.value >= 75 && aiPercent.value < 90);

const packageLabel = computed(() => {
  const name = data.value?.package;
  if (!name) return t('SIDEBAR_MINI_DASHBOARD.PLAN_FREE');
  return name.charAt(0).toUpperCase() + name.slice(1);
});

const trialDaysLeft = computed(() => Number(data.value?.trial_days_left || 0));
const isTrialing = computed(() => data.value?.status === 'trialing' && trialDaysLeft.value > 0);

const dashboardUrl = computed(() => data.value?.dashboard_url || '');

const openDashboard = () => {
  if (dashboardUrl.value) window.open(dashboardUrl.value, '_blank', 'noopener');
};

const shouldRender = computed(() => isEnabled.value && !isLoading.value && data.value);
</script>

<template>
  <div v-if="shouldRender" class="w-full">
    <button
      v-if="isCollapsed"
      type="button"
      class="flex relative justify-center items-center mb-2 w-8 h-8 rounded-lg cursor-pointer text-n-slate-11 hover:bg-n-alpha-1 hover:text-n-slate-12"
      :title="`${packageLabel} · ${aiUsed}/${aiTotal}`"
      @click="openDashboard"
    >
      <Icon icon="i-lucide-gauge" class="w-4 h-4" />
      <span
        v-if="isAiCritical"
        class="absolute top-0.5 ltr:right-0.5 rtl:left-0.5 w-1.5 h-1.5 rounded-full bg-n-ruby-9"
      />
    </button>

    <div
      v-else
      class="flex flex-col gap-2 px-2.5 py-2 mb-2 w-full rounded-lg border bg-n-alpha-1 border-n-weak"
    >
      <div class="flex gap-2 justify-between items-center min-w-0">
        <div class="flex gap-1.5 items-center min-w-0">
          <Icon icon="i-lucide-gauge" class="flex-shrink-0 w-3.5 h-3.5 text-n-slate-11" />
          <span class="text-xs font-medium truncate text-n-slate-12">
            {{ packageLabel }}
          </span>
        </div>
        <span
          v-if="isTrialing"
          class="px-1.5 py-0.5 text-xs font-medium rounded text-n-amber-11 bg-n-amber-3"
        >
          {{ t('SIDEBAR_MINI_DASHBOARD.TRIAL_DAYS', { days: trialDaysLeft }) }}
        </span>
      </div>

      <div v-if="aiTotal > 0" class="flex flex-col gap-1">
        <div class="flex justify-between items-center text-xs text-n-slate-11">
          <span>{{ t('SIDEBAR_MINI_DASHBOARD.AI_USAGE') }}</span>
          <span>{{ aiUsed }}/{{ aiTotal }}</span>
        </div>
        <div class="overflow-hidden w-full h-1 rounded-full bg-n-alpha-2">
          <div
            class="h-full rounded-full transition-all"
            :class="{
              'bg-n-brand': !isAiWarning && !isAiCritical,
              'bg-n-amber-9': isAiWarning,
              'bg-n-ruby-9': isAiCritical,
            }"
            :style="{ width: `${aiPercent}%` }"
          />
        </div>
      </div>

      <Button
        :label="t('SIDEBAR_MINI_DASHBOARD.OPEN_DASHBOARD')"
        size="sm"
        variant="outline"
        slate
        trailing-icon
        icon="i-lucide-external-link"
        class="w-full"
        @click="openDashboard"
      />
    </div>
  </div>
</template>
